/**
 * @file SkuEditDialog.tsx
 * @description Полный редактор SKU (phase08.5 shadcn).
 *
 * phase19.14: секция «Склад» — ячейка по умолчанию (W14) и порог «мало» (W9);
 * поля живут отдельным компонентом `SkuStorageFields`.
 *
 * phase19.22 (ui-design §5): секция «Код и этикетка» (`SkuCodeLabelFields`)
 * над штрихкодом; GTIN виден всегда и проверяется по контрольной цифре
 * (`SkuBarcodeFields`), обязателен — только при маркировке ЧЗ.
 */

import { RefreshCw } from "lucide-react";
import { useState } from "react";

import { Alert } from "../../../../components/ui/alert";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import {
  catalogSkus,
  type CatalogSku,
  type CatalogUnit,
  type CreateSkuInput,
} from "../../../../lib/api-catalog";
import { ApiError } from "../../../../lib/api-client";
import type { LabelTemplate } from "../../../../lib/api-product-kinds";
import { gtinError } from "../../../../lib/product-kind-format";
import { qtyInputProps, validateQty } from "../../../../lib/qty-input";
import { SkuBarcodeFields, type SkuBarcodeValue } from "./SkuBarcodeFields";
import { SkuCodeLabelFields } from "./SkuCodeLabelFields";
import { SkuStorageFields, type SkuStorageValue } from "./SkuStorageFields";

/**
 * Вес нетто и брутто хранятся в граммах — доли грамма в карточке фасовки не
 * заполняют, а шаг 0.001 у стрелки уводил поле в «0.001 г».
 */
const GRAM_FIELD = qtyInputProps("г");

interface Props {
  open: boolean;
  productId: string;
  sku: CatalogSku | null;
  units: CatalogUnit[];
  markingRequired: boolean;
  tnvedHint: string | null;
  /** phase19.22 — шаблон этикетки вида товара (вариант «Как у вида»). */
  kindLabelTemplate: LabelTemplate | null;
  suggestedCode?: string;
  onGenerateCode?: () => string;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

interface Form {
  code: string;
  displayName: string;
  saleQuantity: string;
  saleUnitId: string;
  priceGross: string;
  vatRate: string;
  stockQuantity: string;
  weightG: string;
  lengthMm: string;
  widthMm: string;
  heightMm: string;
  packageWeightG: string;
  harvestYear: string;
  originRegion: string;
  gtin: string;
  tnved: string;
  /** phase19.14 (W14) — ячейка по умолчанию; "" — не задана. */
  defaultBinId: string;
  /** phase19.14 (W9) — порог «мало»; "" — порог не задан. */
  minStock: string;
  /** phase19.22 (W17-5b) — свой шаблон этикетки; "" — как у вида. */
  labelTemplate: "" | LabelTemplate;
}

function init(
  sku: CatalogSku | null,
  tnvedHint: string | null,
  suggestedCode?: string,
): Form {
  return {
    code: sku?.code ?? suggestedCode ?? "",
    displayName: sku?.displayName ?? "",
    saleQuantity: sku?.saleQuantity ?? "",
    saleUnitId: sku?.saleUnitId ?? "",
    priceGross: sku?.priceGross ?? "",
    vatRate: sku?.vatRate?.toString() ?? "",
    stockQuantity: sku?.stockQuantity ?? "0",
    weightG: sku?.weightG ?? "",
    lengthMm: sku?.lengthMm ?? "",
    widthMm: sku?.widthMm ?? "",
    heightMm: sku?.heightMm ?? "",
    packageWeightG: sku?.packageWeightG ?? "",
    harvestYear: sku?.harvestYear?.toString() ?? "",
    originRegion: sku?.originRegion ?? "",
    gtin: sku?.gtin ?? "",
    tnved: sku?.tnved ?? tnvedHint ?? "",
    defaultBinId: sku?.defaultBinId ?? "",
    minStock: sku?.minStock ?? "",
    labelTemplate: sku?.labelTemplate ?? "",
  };
}

const nativeSelectCls =
  "flex h-10 w-full rounded-md bg-input px-3 py-2 text-sm text-on-surface outline-none focus-visible:bg-surface-lowest focus-visible:[box-shadow:inset_0_-2px_0_var(--primary)]";

export function SkuEditDialog({
  open,
  productId,
  sku,
  units,
  markingRequired,
  tnvedHint,
  kindLabelTemplate,
  suggestedCode,
  onGenerateCode,
  onClose,
  onSaved,
}: Props) {
  const notify = useNotify();
  const [form, setForm] = useState<Form>(() => init(sku, tnvedHint, suggestedCode));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function patch<K extends keyof Form>(k: K, v: Form[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.code.trim()) {
      setError("Код SKU обязателен.");
      return;
    }
    if (!form.saleUnitId) {
      setError("Выберите единицу измерения.");
      return;
    }
    const unitCode = units.find((x) => x.id === form.saleUnitId)?.code ?? null;
    // Размер фасовки меряется выбранной единицей: «50 г» целое, «0.5 кг» —
    // нормальный развес. Правило одно на все поля количества (`qty-input`).
    const qtyError = validateQty(form.saleQuantity, unitCode, {
      allowZero: false,
    });
    if (qtyError) {
      setError(`Размер фасовки: ${qtyError}`);
      return;
    }
    const qty = parseFloat(form.saleQuantity);
    // Цену скрывает роль (сервер прислал null, SC51): пустое поле не значит
    // «цена 0» — при правке цену тогда не отправляем вовсе.
    const priceHidden = !!sku && sku.priceGross === null && !form.priceGross.trim();
    const price = parseFloat(form.priceGross);
    if (!priceHidden && (isNaN(price) || price < 0)) {
      setError(form.priceGross.trim() ? "Цена не может быть отрицательной." : "Укажите цену.");
      return;
    }
    // Остаток считается фасовками, вес — целыми граммами: дробь здесь всегда
    // опечатка ввода, а не намерение.
    for (const [label, value, unit] of [
      ["Остаток", form.stockQuantity, "шт"],
      ["Вес нетто", form.weightG, "г"],
      ["Вес брутто", form.packageWeightG, "г"],
    ] as const) {
      if (!value.trim()) continue;
      const message = validateQty(value, unit);
      if (message) {
        setError(`${label}: ${message}`);
        return;
      }
    }
    if (markingRequired && (!form.gtin.trim() || !form.tnved.trim())) {
      setError("Для этой категории нужны GTIN и ТН ВЭД.");
      return;
    }
    // Опечатку в GTIN ловим до сервера (400 sku_gtin_invalid) — текст тот же,
    // что под полем. Как и сервер, проверяем только изменённый GTIN: старый
    // код без контрольной цифры не должен запирать правку остальных полей.
    const gtinChanged = form.gtin.trim() !== (sku?.gtin ?? "").trim();
    const gtinProblem = gtinChanged ? gtinError(form.gtin) : null;
    if (gtinProblem) {
      setError(gtinProblem);
      return;
    }

    setSubmitting(true);
    try {
      const payload: CreateSkuInput = {
        code: form.code.trim(),
        displayName: form.displayName.trim() || undefined,
        saleQuantity: qty,
        saleUnitId: form.saleUnitId,
        priceGross: price,
        vatRate: form.vatRate ? parseInt(form.vatRate, 10) : undefined,
        stockQuantity: parseFloat(form.stockQuantity) || 0,
        weightG: form.weightG ? parseFloat(form.weightG) : undefined,
        lengthMm: form.lengthMm ? parseFloat(form.lengthMm) : undefined,
        widthMm: form.widthMm ? parseFloat(form.widthMm) : undefined,
        heightMm: form.heightMm ? parseFloat(form.heightMm) : undefined,
        packageWeightG: form.packageWeightG
          ? parseFloat(form.packageWeightG)
          : undefined,
        harvestYear: form.harvestYear ? parseInt(form.harvestYear, 10) : undefined,
        originRegion: form.originRegion.trim() || undefined,
        gtin: form.gtin.trim() || undefined,
        tnved: form.tnved.trim() || undefined,
        // null (а не undefined) — явный сброс: сервер различает «не трогать» и
        // «снять значение» (см. update-sku.dto.ts).
        defaultBinId: form.defaultBinId || null,
        minStock: form.minStock.trim() ? Number(form.minStock) : null,
        // "" — «как у вида»: null явно сбрасывает свой шаблон.
        labelTemplate: form.labelTemplate || null,
      };
      if (sku) {
        // Скрытую цену не трогаем: без поля сервер её не меняет.
        const { priceGross: _hiddenPrice, ...withoutPrice } = payload;
        await catalogSkus.update(productId, sku.id, priceHidden ? withoutPrice : payload);
        notify.success("SKU обновлён");
      } else {
        await catalogSkus.create(productId, payload);
        notify.success("SKU создан");
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSubmitting(false);
    }
  }

  const u = units.find((x) => x.id === form.saleUnitId);
  // Шаг поля «Размер фасовки» — по выбранной единице: «г»/«мл»/«шт» целые,
  // «кг»/«л» дробные. Единица не выбрана — шаг 1 (реестр отдаёт фолбэк).
  const qtyProps = qtyInputProps(u?.code);
  const qtyHelp = !u
    ? "Выберите единицу справа →"
    : u.code === "pcs" || u.code === "pack" || u.code === "bunch"
      ? `Сколько ${u.displayName.toLowerCase()}`
      : `В ${u.displayName.toLowerCase()}х (напр. 100)`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{sku ? `SKU «${sku.code}»` : "Новый вариант SKU"}</DialogTitle>
        </DialogHeader>

        <form
          id="sku-form"
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-col gap-4 overflow-y-auto"
        >
          <div>
            <div className="label-editorial mb-3">Артикул и название</div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="sku-code">Артикул *</Label>
                <div className="flex gap-2">
                  <Input
                    id="sku-code"
                    value={form.code}
                    onChange={(e) => patch("code", e.target.value)}
                    disabled={submitting}
                    required
                    placeholder="MILK-260528-K7X3"
                  />
                  {onGenerateCode ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => patch("code", onGenerateCode())}
                      disabled={submitting}
                      aria-label="Сгенерировать"
                    >
                      <RefreshCw className="size-4" />
                    </Button>
                  ) : null}
                </div>
                <p className="text-xs text-on-surface-variant">
                  Формат «{`{ПРЕФИКС}-{ГГММДД}-{RAND4}`}» или любой ваш. Уникален в рамках товара.
                </p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="sku-name">Название варианта</Label>
                <Input
                  id="sku-name"
                  value={form.displayName}
                  onChange={(e) => patch("displayName", e.target.value)}
                  disabled={submitting}
                  placeholder='Напр.: «50 г», «Большая упаковка»'
                />
              </div>
            </div>
          </div>

          <div>
            <div className="label-editorial mb-3">Фасовка</div>
            <div className="flex gap-3">
              <div className="flex-1 flex flex-col gap-1.5">
                <Label htmlFor="sku-qty">Размер фасовки *</Label>
                <Input
                  id="sku-qty"
                  type="number"
                  step={qtyProps.step}
                  inputMode={qtyProps.inputMode}
                  min={qtyProps.step}
                  value={form.saleQuantity}
                  onChange={(e) => patch("saleQuantity", e.target.value)}
                  disabled={submitting}
                  required
                />
                <p className="text-xs text-on-surface-variant">{qtyHelp}</p>
              </div>
              <div className="flex-1 flex flex-col gap-1.5">
                <Label htmlFor="sku-unit">Единица *</Label>
                <select
                  id="sku-unit"
                  value={form.saleUnitId}
                  onChange={(e) => patch("saleUnitId", e.target.value)}
                  disabled={submitting}
                  className={nativeSelectCls}
                  required
                >
                  <option value="">—</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.displayName} ({u.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <div className="label-editorial mb-3">Цена и остаток</div>
            <div className="flex gap-3">
              <div className="flex-[1.2] flex flex-col gap-1.5">
                <Label htmlFor="sku-price">Цена (₽) *</Label>
                <Input
                  id="sku-price"
                  type="number"
                  step={0.01}
                  min={0}
                  value={form.priceGross}
                  onChange={(e) => patch("priceGross", e.target.value)}
                  disabled={submitting}
                  // Цену скрывает роль (null с сервера): поле не обязательно, «—».
                  required={sku?.priceGross !== null}
                  placeholder={sku?.priceGross === null ? "—" : undefined}
                />
              </div>
              <div className="flex-[0.8] flex flex-col gap-1.5">
                <Label htmlFor="sku-vat">НДС (%)</Label>
                <Input
                  id="sku-vat"
                  type="number"
                  value={form.vatRate}
                  onChange={(e) => patch("vatRate", e.target.value)}
                  disabled={submitting}
                />
                <p className="text-xs text-on-surface-variant">пусто = УСН</p>
              </div>
              <div className="flex-1 flex flex-col gap-1.5">
                <Label htmlFor="sku-stock">Остаток, шт</Label>
                <Input
                  id="sku-stock"
                  type="number"
                  step={1}
                  inputMode="numeric"
                  min={0}
                  value={form.stockQuantity}
                  onChange={(e) => patch("stockQuantity", e.target.value)}
                  disabled={submitting}
                />
              </div>
            </div>
          </div>

          <div>
            <div className="label-editorial mb-3">
              Габариты упаковки (для расчёта доставки)
            </div>
            <div className="flex gap-2">
              {(
                [
                  ["lengthMm", "Длина (мм)"],
                  ["widthMm", "Ширина (мм)"],
                  ["heightMm", "Высота (мм)"],
                ] as const
              ).map(([k, label]) => (
                <div key={k} className="flex-1 flex flex-col gap-1.5">
                  <Label htmlFor={`sku-${k}`}>{label}</Label>
                  <Input
                    id={`sku-${k}`}
                    type="number"
                    step={0.1}
                    min={0}
                    value={form[k]}
                    onChange={(e) => patch(k, e.target.value)}
                    disabled={submitting}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <div className="flex-1 flex flex-col gap-1.5">
                <Label htmlFor="sku-wnet">Вес нетто (г)</Label>
                <Input
                  id="sku-wnet"
                  type="number"
                  step={GRAM_FIELD.step}
                  inputMode={GRAM_FIELD.inputMode}
                  min={0}
                  value={form.weightG}
                  onChange={(e) => patch("weightG", e.target.value)}
                  disabled={submitting}
                />
                <p className="text-xs text-on-surface-variant">Содержимое</p>
              </div>
              <div className="flex-1 flex flex-col gap-1.5">
                <Label htmlFor="sku-wgross">Вес брутто (г)</Label>
                <Input
                  id="sku-wgross"
                  type="number"
                  step={GRAM_FIELD.step}
                  inputMode={GRAM_FIELD.inputMode}
                  min={0}
                  value={form.packageWeightG}
                  onChange={(e) => patch("packageWeightG", e.target.value)}
                  disabled={submitting}
                />
                <p className="text-xs text-on-surface-variant">С упаковкой</p>
              </div>
            </div>
          </div>

          <SkuStorageFields
            value={{ defaultBinId: form.defaultBinId, minStock: form.minStock }}
            onChange={(next: SkuStorageValue) =>
              setForm((f) => ({ ...f, ...next }))
            }
            disabled={submitting}
          />

          <div>
            <div className="label-editorial mb-3">Происхождение</div>
            <div className="flex gap-3">
              <div className="flex-1 flex flex-col gap-1.5">
                <Label htmlFor="sku-year">Год урожая</Label>
                <Input
                  id="sku-year"
                  type="number"
                  min={1900}
                  max={2100}
                  value={form.harvestYear}
                  onChange={(e) => patch("harvestYear", e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="flex-[2] flex flex-col gap-1.5">
                <Label htmlFor="sku-region">Регион сбора</Label>
                <Input
                  id="sku-region"
                  value={form.originRegion}
                  onChange={(e) => patch("originRegion", e.target.value)}
                  disabled={submitting}
                />
              </div>
            </div>
          </div>

          <SkuCodeLabelFields
            internalCode={sku?.internalCode ?? null}
            value={form.labelTemplate}
            onChange={(v) => patch("labelTemplate", v)}
            kindTemplate={kindLabelTemplate}
            disabled={submitting}
          />

          <SkuBarcodeFields
            value={{ gtin: form.gtin, tnved: form.tnved }}
            onChange={(next: Partial<SkuBarcodeValue>) => setForm((f) => ({ ...f, ...next }))}
            markingRequired={markingRequired}
            tnvedHint={tnvedHint}
            originalGtin={sku?.gtin ?? null}
            disabled={submitting}
          />

          {error ? <Alert severity="error">{error}</Alert> : null}
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button type="submit" form="sku-form" disabled={submitting}>
            {submitting ? "Сохраняем…" : sku ? "Сохранить" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
